import { useRef, useState } from 'react';
import { Upload, X, FileText } from 'lucide-react';
import { CANAIS, TIPOS } from '../../lib/constants';

const EMPTY = {
  titulo: '',
  objetivo: '',
  publico_alvo: '',
  mensagem_chave: '',
  data_acao: '',
  data_acao_fim: '',
  data_postagem: '',
  canais: [],
  tipos: [],
  meio_instagram: '',
  meio_portais: '',
  meio_radio: '',
  meio_tv: '',
  detalhes: '',
  observacoes: '',
  arquivo_url: '',
  arte_local: '',
  arte_hora: '',
  arte_logos: '',
};

const NAO_INFORMADO = 'Não informado';
const ARTE_CAMPOS = ['arte_local', 'arte_hora', 'arte_logos'];

function toggle(arr, v) {
  return arr.includes(v) ? arr.filter((x) => x !== v) : [...arr, v];
}

// Campo que não pode ficar em branco: ou tem valor, ou marca "Não informado"
// explicitamente. Pedido da Comunicação — em branco deixa o editor sem saber
// se o dado existe ou só não foi preenchido ainda.
function CampoObrigatorioOuNaoInformado({ label, value, onChange, placeholder }) {
  const naoInformado = value === NAO_INFORMADO;
  return (
    <div className="field">
      <label>{label} *</label>
      <div className="row" style={{ gap: 8, alignItems: 'center' }}>
        <input
          className="input"
          placeholder={placeholder}
          value={naoInformado ? '' : value}
          disabled={naoInformado}
          onChange={(e) => onChange(e.target.value)}
          style={{ flex: 1 }}
        />
        <label className="row" style={{ gap: 4, fontSize: 12, whiteSpace: 'nowrap', cursor: 'pointer' }}>
          <input
            type="checkbox"
            checked={naoInformado}
            onChange={(e) => onChange(e.target.checked ? NAO_INFORMADO : '')}
            style={{ accentColor: 'var(--c-primary)' }}
          />
          Não informado
        </label>
      </div>
    </div>
  );
}

export function DemandaForm({ initial, secretarias, mostrarSecretaria = false, onSubmit, onCancel, submitLabel = 'Enviar demanda' }) {
  const [form, setForm] = useState({ ...EMPTY, ...(initial || {}) });
  const [secretariaId, setSecretariaId] = useState(initial?.secretaria_id || '');
  const [arquivos, setArquivos] = useState([]);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');
  const [progresso, setProgresso] = useState(null);
  const fileRef = useRef(null);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const adicionarArquivos = (fileList) => {
    setArquivos((prev) => [...prev, ...Array.from(fileList || [])]);
  };
  const removerArquivo = (idx) => setArquivos((prev) => prev.filter((_, i) => i !== idx));

  const submit = async (e) => {
    e.preventDefault();
    setErr('');
    if (!form.titulo.trim() || !form.objetivo.trim()) {
      setErr('Preencha título e objetivo.');
      return;
    }
    // Validação cliente-side: data fim não pode ser anterior à de início
    if (form.data_acao && form.data_acao_fim && form.data_acao_fim < form.data_acao) {
      setErr('Data final da ação não pode ser anterior à data de início.');
      return;
    }
    if (form.tipos.includes('arte') && ARTE_CAMPOS.some((k) => !String(form[k] || '').trim())) {
      setErr('Para demandas de arte, preencha local, hora e logos (ou marque "Não informado").');
      return;
    }
    setSaving(true);
    try {
      const payload = { ...form };
      if (mostrarSecretaria && secretariaId) payload.secretaria_id = parseInt(secretariaId, 10);
      // normalizar datas vazias
      ['data_acao', 'data_acao_fim', 'data_postagem'].forEach((k) => { if (!payload[k]) payload[k] = null; });
      await onSubmit(payload, arquivos, setProgresso);
    } catch (e) {
      setErr(e.data?.error || e.message || 'Erro ao salvar');
      setProgresso(null);
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={submit} className="stack" style={{ gap: 18 }}>
      {mostrarSecretaria && (
        <div className="field">
          <label>Secretaria solicitante *</label>
          <select className="select" required value={secretariaId} onChange={(e) => setSecretariaId(e.target.value)}>
            <option value="">Selecione...</option>
            {secretarias.map((s) => <option key={s.id} value={s.id}>{s.nome}</option>)}
          </select>
        </div>
      )}

      <div className="field">
        <label>Título da demanda *</label>
        <input
          className="input"
          required
          maxLength={200}
          placeholder="Ex: Vídeo Ultrassom na UBS Central"
          value={form.titulo}
          onChange={(e) => update('titulo', e.target.value)}
        />
      </div>

      <div className="field">
        <label>Objetivo da comunicação *</label>
        <textarea
          className="textarea"
          required
          placeholder="Descreva o que precisa ser comunicado, detalhes da ação, contexto..."
          value={form.objetivo}
          onChange={(e) => update('objetivo', e.target.value)}
        />
      </div>

      <div className="field">
        <label>Data da ação</label>
        <div className="grid-2" style={{ gap: 8 }}>
          <input
            type="date"
            className="input"
            value={form.data_acao || ''}
            onChange={(e) => update('data_acao', e.target.value)}
            placeholder="Início"
          />
          <input
            type="date"
            className="input"
            value={form.data_acao_fim || ''}
            min={form.data_acao || undefined}
            onChange={(e) => update('data_acao_fim', e.target.value)}
            placeholder="Fim (opcional)"
            disabled={!form.data_acao}
          />
        </div>
        <p className="text-xs muted" style={{ marginTop: 4 }}>
          Preencha a data <strong>de início</strong>. Se a ação durar vários dias (ex.: mutirão de 3 dias, semana de ultrassom),
          preencha também a <strong>data de fim</strong>.
        </p>
      </div>

      <div className="field">
        <label>Data prevista de postagem</label>
        <input type="date" className="input" value={form.data_postagem || ''} onChange={(e) => update('data_postagem', e.target.value)} />
      </div>

      <div className="field">
        <label>Público-alvo</label>
        <input
          className="input"
          placeholder="Ex: moradores do bairro, empresários..."
          value={form.publico_alvo || ''}
          onChange={(e) => update('publico_alvo', e.target.value)}
        />
      </div>

      <div className="field">
        <label>Mensagem-chave</label>
        <input
          className="input"
          placeholder="Em uma frase, o que deve ficar claro na comunicação?"
          value={form.mensagem_chave || ''}
          onChange={(e) => update('mensagem_chave', e.target.value)}
        />
      </div>

      <div className="field">
        <label>Canais</label>
        <div className="row-wrap">
          {CANAIS.map((c) => (
            <label key={c.key} className="row" style={{ gap: 6, cursor: 'pointer', padding: '6px 10px', border: '1px solid var(--c-border)', borderRadius: 20, background: form.canais.includes(c.key) ? 'var(--c-primary-soft)' : 'var(--c-surface)', color: form.canais.includes(c.key) ? 'var(--c-primary)' : 'var(--c-text)', fontSize: 13 }}>
              <input
                type="checkbox"
                checked={form.canais.includes(c.key)}
                onChange={() => update('canais', toggle(form.canais, c.key))}
                style={{ accentColor: 'var(--c-primary)' }}
              />
              {c.label}
            </label>
          ))}
        </div>
      </div>

      <div className="field">
        <label>Tipos de conteúdo</label>
        <div className="row-wrap">
          {TIPOS.map((t) => (
            <label key={t.key} className="row" style={{ gap: 6, cursor: 'pointer', padding: '6px 10px', border: '1px solid var(--c-border)', borderRadius: 20, background: form.tipos.includes(t.key) ? 'var(--c-primary-soft)' : 'var(--c-surface)', color: form.tipos.includes(t.key) ? 'var(--c-primary)' : 'var(--c-text)', fontSize: 13 }}>
              <input
                type="checkbox"
                checked={form.tipos.includes(t.key)}
                onChange={() => update('tipos', toggle(form.tipos, t.key))}
                style={{ accentColor: 'var(--c-primary)' }}
              />
              {t.label}
            </label>
          ))}
        </div>
      </div>

      {form.tipos.includes('arte') && (
        <div className="stack" style={{ gap: 10, padding: 12, border: '1px dashed var(--c-border)', borderRadius: 8 }}>
          <p className="text-xs muted strong" style={{ textTransform: 'uppercase', letterSpacing: 0.4, margin: 0 }}>
            Necessário para montar a arte
          </p>
          <CampoObrigatorioOuNaoInformado
            label="Local"
            placeholder="Ex: Praça de Esportes Zezé Cota"
            value={form.arte_local}
            onChange={(v) => update('arte_local', v)}
          />
          <CampoObrigatorioOuNaoInformado
            label="Hora"
            placeholder="Ex: 14h"
            value={form.arte_hora}
            onChange={(v) => update('arte_hora', v)}
          />
          <CampoObrigatorioOuNaoInformado
            label="Logos"
            placeholder="Ex: Prefeitura + Secretaria de Cultura"
            value={form.arte_logos}
            onChange={(v) => update('arte_logos', v)}
          />
        </div>
      )}

      <div className="field">
        <label>Detalhes adicionais</label>
        <textarea
          className="textarea"
          placeholder="Horários, pessoas envolvidas, locais, links de referência..."
          value={form.detalhes || ''}
          onChange={(e) => update('detalhes', e.target.value)}
        />
      </div>

      {/* Anexar imagens — o input nativo continua dizendo "Nenhum arquivo
          escolhido" depois da escolha (limpamos o value pra permitir escolher
          o mesmo arquivo de novo), o que fazia parecer que nada foi anexado.
          Por isso ele fica escondido atrás de um botão, com preview do que
          está de fato na lista. */}
      <div className="field">
        <label>Anexar imagens</label>
        <input
          ref={fileRef}
          type="file"
          accept="image/*,application/pdf"
          multiple
          style={{ display: 'none' }}
          onChange={(e) => { adicionarArquivos(e.target.files); e.target.value = ''; }}
        />
        <div className="row" style={{ gap: 8 }}>
          <button type="button" className="btn btn-secondary btn-sm" onClick={() => fileRef.current?.click()} disabled={saving}>
            <Upload size={13} /> Escolher imagens
          </button>
          <span className="text-xs muted" style={{ alignSelf: 'center' }}>
            {arquivos.length === 0
              ? 'Imagem ou PDF · até 20MB cada'
              : `${arquivos.length} arquivo${arquivos.length === 1 ? '' : 's'} pronto${arquivos.length === 1 ? '' : 's'} para enviar`}
          </span>
        </div>
        {arquivos.length > 0 && (
          <div className="anexo-grid" style={{ marginTop: 10 }}>
            {arquivos.map((f, i) => (
              <div key={`${f.name}-${i}`} className="anexo-item">
                <div className="anexo-thumb anexo-thumb-file">
                  {f.type.startsWith('image/')
                    ? <img src={URL.createObjectURL(f)} alt={f.name} />
                    : <><FileText size={26} /><span className="text-xs" style={{ marginTop: 4 }}>PDF</span></>}
                </div>
                <div className="anexo-meta">
                  <span className="anexo-nome" title={f.name}>{f.name}</span>
                  <span className="text-xs muted">{Math.max(1, Math.round(f.size / 1024))} KB</span>
                </div>
                <div className="anexo-acoes">
                  <button type="button" className="btn btn-ghost btn-sm" style={{ padding: '2px 8px', minHeight: 24, fontSize: 11 }} onClick={() => removerArquivo(i)}>
                    <X size={12} /> Remover
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
        {progresso && (
          <div style={{ marginTop: 8 }}>
            <div style={{ height: 4, background: 'var(--c-border)', borderRadius: 2, overflow: 'hidden' }}>
              <div style={{ width: `${progresso.pct}%`, height: '100%', background: 'var(--c-primary)', transition: 'width .2s' }} />
            </div>
            <div className="text-xs muted">
              Enviando imagem {progresso.indice}/{progresso.total} — {progresso.nome} ({progresso.pct}%)
            </div>
          </div>
        )}
      </div>

      <div className="field">
        <label>Link de arquivo/referência (Drive, YouTube, etc.) — opcional</label>
        <input
          className="input"
          type="url"
          placeholder="https://..."
          value={form.arquivo_url || ''}
          onChange={(e) => update('arquivo_url', e.target.value)}
        />
      </div>

      {err && <div style={{ color: 'var(--c-danger)', fontSize: 13 }}>{err}</div>}

      <div className="row" style={{ justifyContent: 'flex-end', gap: 10 }}>
        {onCancel && <button type="button" className="btn btn-secondary" onClick={onCancel} disabled={saving}>Cancelar</button>}
        <button type="submit" className="btn btn-primary" disabled={saving}>
          {saving ? <><span className="spinner" /> Enviando...</> : submitLabel}
        </button>
      </div>
    </form>
  );
}
